/**
 * ## Categories for internal transfers
 *
 *---
 *
 * ### Notes:
 *
 * * **Originally** replicated from cornerstone
 * * A refactor [MF-399](https://spruceco.atlassian.net/browse/MF-399) deprecated all [legacyValues]
 * except for [other] and created a new set of [activeValues] to be used for new records. _Existing
 * records **will not be migrated** so [legacyValues] will continue to be supported for reading.
 *
 *---
 *
 * This enum is also implemented in the following places:
 * * [timber](https://github.com/spruceholdings/timber/blob/main/src/main/kotlin/co/spruce/timber/model/escrow/InternalTransferCategory.kt) -- `InternalTransferCategory.kt`
 * * [spruce_spa](https://github.com/spruceholdings/spruce_spa/blob/main/spa/src/types/accounting/response/eInternalTransferCategory.ts) -- `eInternalTransferCategory.ts`
 * * [spruce_services](https://github.com/spruceholdings/spruce_services/blob/main/cornerstone/src/main/kotlin/co/spruce/db/postgres/escrow/Enums.kt) `Enums.kt` as **ExpectedInternalTransferCategory**
 *
 *---
 *
 * The following categories are marked as _deprecated_ and should not be used for **new** records.
 * * deed_tax
 * * holdback
 * * mortgage_tax
 * * pass_through
 * * property_taxes
 * * recording
 * * reimbursement
 * * remittance
 * * revenue
 * * sales_tax
 */
// maybe this belongs in ui toolkit
import { ISelectOption } from 'ui-toolkit';

export enum eInternalTransferCategory {
  AchTransfer = 'ach_transfer',
  BulkOrdersEMDRefundTransfers = 'bulk_orders_emd_refund_transfers',
  CorrectingTransferRecording = 'correcting_transfer_recording',
  CorrectingTransferReimbursement = 'correcting_transfer_reimbursement',
  CorrectingTransferRemittance = 'correcting_transfer_remittance',
  CorrectingTransferRevenue = 'correcting_transfer_revenue',
  FundingDelayed = 'funding_delayed',
  FundingError = 'funding_error',
  FundsInIncorrectAccount = 'funds_in_incorrect_account',
  RecordingMailOutCountry = 'recording_mail_out_county',
  RecordingRefund = 'recording_refund',
  RefundOverCollectedTitleFee = 'refund_over_collected_title_fee',
  ShortfallBorrowerFunds = 'shortfall_borrower_funds',
  ShortfallClientExperience = 'shortfall_client_experience',
  ShortfallFeeNotCollectedAtClosing = 'shortfall_fee_not_collected_at_closing',
  ShortfallFilling_Fee = 'shortfall_filling_fee',
  ShortfallHOI = 'shortfall_hoi',
  ShortfallLenderFunds = 'shortfall_lender_funds',
  ShortfallPayoff = 'shortfall_payoff',
  ShortfallPenaltiesInterest = 'shortfall_penalties_interest',
  ShortfallPropertyTaxes = 'shortfall_property_taxes',
  ShortfallRecording = 'shortfall_recording',
  ShortfallRemittance = 'shortfall_remittance',
  ShortfallReturnedCheckFee = 'shortfall_returned_check_fee',
  ShortfallReturnedWireFee = 'shortfall_returned_wire_fee',
  ShortfallSalesTax = 'shortfall_sales_tax',
  ShortfallTitleFees = 'shortfall_title_fees',
  ShortfallTransferTax = 'shortfall_transfer_tax',
  ShortfallUpfrontFee = 'shortfall_upfront_fee',
  ShortfallUpfrontHOA = 'shortfall_upfront_hoa',
  ShortfallUpfrontRecording = 'shortfall_upfront_recording',
  ShortfallUpfrontSubordination = 'shortfall_upfront_subordination',
  ShortfallWorkshare = 'shortfall_workshare',
  UpfrontFee = 'upfront_fee',
  UnwindDisbursement = 'unwind_disbursement',
  TemporaryShortfall = 'temporary_shortfall',

  /**
   * This category was retained in its original form during the refactor in MF-399.
   */
  other = 'other',

  // --------------------------------------------------------------------------------------------
  // ## Legacy values
  //
  // With [MF-399](https://spruceco.atlassian.net/browse/MF-399) this enumeration was refactored
  // to better match processes used.
  // --------------------------------------------------------------------------------------------
  CorrectingTransfer = 'correcting_transfer',
  deed_tax = 'deed_tax',
  holdback = 'holdback',
  mortgage_tax = 'mortgage_tax',
  pass_through = 'pass_through',
  property_taxes = 'property_taxes',
  recording = 'recording',
  reimbursement = 'reimbursement',
  remittance = 'remittance',
  revenue = 'revenue',
  sales_tax = 'sales_tax',
}

export const prettyInternalTransferCategory: Record<eInternalTransferCategory, string> = {
  ach_transfer: 'ACH Transfer',
  bulk_orders_emd_refund_transfers: 'Bulk Orders - EMD refund transfers',
  correcting_transfer_recording: 'Correcting Transfer-Recording',
  correcting_transfer_reimbursement: 'Correcting Transfer-Reimbursement',
  correcting_transfer_remittance: 'Correcting Transfer-Remittance',
  correcting_transfer_revenue: 'Correcting Transfer-Revenue',
  funding_delayed: 'Funding Delayed',
  funding_error: 'Funding Error',
  funds_in_incorrect_account: 'Funds in incorrect account',
  recording_mail_out_county: 'Recording - mail out county',
  recording_refund: 'Recording - refund',
  refund_over_collected_title_fee: 'Refund - Over Collected Title Fee',
  shortfall_borrower_funds: 'Shortfall - borrower funds',
  shortfall_client_experience: 'Shortfall - client experience',
  shortfall_fee_not_collected_at_closing: 'Shortfall - Fee not collected at closing',
  shortfall_filling_fee: 'Shortfall - Filling Fee',
  shortfall_hoi: 'Shortfall - HOI',
  shortfall_lender_funds: 'Shortfall - lender funds',
  shortfall_payoff: 'Shortfall - payoff',
  shortfall_penalties_interest: 'Shortfall - penalties/interest',
  shortfall_property_taxes: 'Shortfall - property taxes',
  shortfall_recording: 'Shortfall - recording',
  shortfall_remittance: 'Shortfall - remittance',
  shortfall_returned_check_fee: 'Shortfall - Returned Check Fee',
  shortfall_returned_wire_fee: 'Shortfall - returned wire fee',
  shortfall_sales_tax: 'Shortfall - sales tax',
  shortfall_title_fees: 'Shortfall - title fees',
  shortfall_transfer_tax: 'Shortfall - transfer tax',
  shortfall_upfront_fee: 'Shortfall - Upfront fee',
  shortfall_upfront_hoa: 'Shortfall - upfront HOA',
  shortfall_upfront_recording: 'Shortfall - upfront recording',
  shortfall_upfront_subordination: 'Shortfall - upfront subordination',
  shortfall_workshare: 'Shortfall - Workshare',
  upfront_fee: 'Upfront Fee',
  unwind_disbursement: 'Unwind Disbursement',
  temporary_shortfall: 'Temporary Shortfall',

  other: 'Other',

  correcting_transfer: 'Correcting Transfer',
  deed_tax: 'Deed Tax',
  holdback: 'Holdback',
  mortgage_tax: 'Mortgage Tax',
  pass_through: 'Pass Through',
  property_taxes: 'Property Taxes',
  recording: 'Recording',
  reimbursement: 'Reimbursement',
  remittance: 'Remittance',
  revenue: 'Revenue',
  sales_tax: 'Sales Tax',
};

export const legacyInternalTransferCategories: eInternalTransferCategory[] = [
  eInternalTransferCategory.CorrectingTransfer,
  eInternalTransferCategory.deed_tax,
  eInternalTransferCategory.holdback,
  eInternalTransferCategory.mortgage_tax,
  eInternalTransferCategory.pass_through,
  eInternalTransferCategory.property_taxes,
  eInternalTransferCategory.recording,
  eInternalTransferCategory.reimbursement,
  eInternalTransferCategory.remittance,
  eInternalTransferCategory.revenue,
  eInternalTransferCategory.sales_tax,
  eInternalTransferCategory.other,
];
export const activeInternalTransferCategories: eInternalTransferCategory[] = [
  eInternalTransferCategory.AchTransfer,
  eInternalTransferCategory.BulkOrdersEMDRefundTransfers,
  eInternalTransferCategory.CorrectingTransferRecording,
  eInternalTransferCategory.CorrectingTransferReimbursement,
  eInternalTransferCategory.CorrectingTransferRemittance,
  eInternalTransferCategory.CorrectingTransferRevenue,
  eInternalTransferCategory.FundingDelayed,
  eInternalTransferCategory.FundingError,
  eInternalTransferCategory.FundsInIncorrectAccount,
  eInternalTransferCategory.RecordingMailOutCountry,
  eInternalTransferCategory.RecordingRefund,
  eInternalTransferCategory.RefundOverCollectedTitleFee,
  eInternalTransferCategory.ShortfallBorrowerFunds,
  eInternalTransferCategory.ShortfallClientExperience,
  eInternalTransferCategory.ShortfallFeeNotCollectedAtClosing,
  eInternalTransferCategory.ShortfallFilling_Fee,
  eInternalTransferCategory.ShortfallHOI,
  eInternalTransferCategory.ShortfallLenderFunds,
  eInternalTransferCategory.ShortfallPayoff,
  eInternalTransferCategory.ShortfallPenaltiesInterest,
  eInternalTransferCategory.ShortfallPropertyTaxes,
  eInternalTransferCategory.ShortfallRecording,
  eInternalTransferCategory.ShortfallRemittance,
  eInternalTransferCategory.ShortfallReturnedCheckFee,
  eInternalTransferCategory.ShortfallReturnedWireFee,
  eInternalTransferCategory.ShortfallSalesTax,
  eInternalTransferCategory.ShortfallTitleFees,
  eInternalTransferCategory.ShortfallTransferTax,
  eInternalTransferCategory.ShortfallUpfrontFee,
  eInternalTransferCategory.ShortfallUpfrontHOA,
  eInternalTransferCategory.ShortfallUpfrontRecording,
  eInternalTransferCategory.ShortfallUpfrontSubordination,
  eInternalTransferCategory.ShortfallWorkshare,
  eInternalTransferCategory.UpfrontFee,
  eInternalTransferCategory.UnwindDisbursement,
  eInternalTransferCategory.TemporaryShortfall,
  eInternalTransferCategory.other,
];

export const categoriesRequiringMemo = [
  eInternalTransferCategory.CorrectingTransfer,
  eInternalTransferCategory.CorrectingTransferRecording,
  eInternalTransferCategory.CorrectingTransferReimbursement,
  eInternalTransferCategory.CorrectingTransferRemittance,
  eInternalTransferCategory.CorrectingTransferRevenue,
  eInternalTransferCategory.other,
];

export const internalTransferCategoryOptions: ISelectOption[] = activeInternalTransferCategories.map((category) => ({
  label: prettyInternalTransferCategory[category],
  value: category,
}));

export const legacyInternalTransferCategoriesToOptions = (
  categories: eInternalTransferCategory[],
  disabledCategories: eInternalTransferCategory[]
) =>
  // JET-162. Operations asked that we full-out remove categories rather than disable them.
  categories
    .filter((category) => !disabledCategories.includes(category))
    .map((category) => ({
      label: prettyInternalTransferCategory[category],
      value: category,
    }));
